export default class ExoForm {
    constructor($form) {
        this.$el = $form
        this.subscribers = []
        //this.initFields();
        $form.on('submit', this.submit.bind(this))
        // Back button restores the page from cache with _sent still true.
        window.addEventListener('pageshow', () => { this._sent = false })
    }

    onSubmit(callback) {
        if (!callback) return
        this.subscribers.push(callback)
    }

    async submit(event) {
        event.preventDefault();
        if (this._sent) return
        if (this.captchaType === 'altcha' && !this.altchaReady) return

        // The form is submitted below via the native form.submit(), which skips
        // HTML5 constraint validation entirely — that let empty required fields
        // and malformed emails through to the server. Check explicitly first and
        // let the browser show its own messages.
        if (!this._reportValidity()) return

        await this.beforeSubmit()
        let result = await this.callSubscribers(event)
        if (result === false) return
        // One submit per page load (checked at the top). A submit can wait seconds
        // on the server (the e-mail check), and a second click sent the same address
        // again: both passed "no account yet" and the slower one hit user.unique (500).
        this._sent = true
        this.$submitBtn?.addClass('ff-btn-disabled')
        // ponytail: released after 30s (CaptainVerify times out at 15s) so a
        // stopped page load can be retried without a reload.
        setTimeout(() => { this._sent = false }, 30000)
        this.$el[0].submit()
    }

    /**
     * Run the browser's constraint validation and surface the first problem.
     * Returns true when the form may be submitted.
     */
    _reportValidity() {
        const form = this.$el[0]
        if (typeof form.reportValidity !== 'function') return true
        if (form.checkValidity()) return true

        this._highlightInvalidFields()
        form.reportValidity()
        return false
    }

    getFormId() {
        return +this.$el.data('form_id')
    }

    getFormName() {
        return this.$el.data('form_name')
    }

    async callSubscribers(event) {
        for (let subscriber of this.subscribers) {
            let result = await subscriber(event, this.$el.get(0))
            if (result === false) return false
        }
    }

    initCaptcha() {

        // Altcha — hidden onload mode with smart submit button state machine
        let $altcha = this.$el.find('altcha-widget');

        // Claim the type BEFORE the gate is wired: the gate's first
        // _updateSubmitState() asks whether this form waits for Altcha, and reading a
        // not-yet-set captchaType would briefly enable a button that should be waiting.
        if ($altcha.length > 0) {
            this.captchaType = 'altcha'
            this.altchaReady = false
        }

        // The submit gate runs on EVERY form, with or without a captcha. It used to be
        // set up only inside the Altcha branch below, so a form without Altcha had no
        // gating at all — its button was clickable while required fields were empty or
        // malformed. Wire it first, then let a captcha add its own condition on top.
        this._initSubmitGate()

        if ($altcha.length > 0) {
            this._initAltchaStateMachine()
            this._updateSubmitState()
            return
        }

        // Fallback: Google reCAPTCHA v3
        let $placeholder = this.$el.find('.g-recaptcha');
        if ($placeholder.length < 1) return
        let siteKey = $placeholder.data('site-key')

        this.captchaWidgetID = grecaptcha.render(
            $placeholder.get(0),
            {
                'sitekey': siteKey,
                'size': 'invisible'
            });
        this.captchaType = 'recaptcha'
    }

    /**
     * Keep the submit button disabled until every required field is VALID.
     *
     * Field-type agnostic: it asks each `[required]` element for its own validity, so
     * an email must look like an email, a `pattern` must match, and so on — the field
     * declares its rule, this just reads the verdict. Nothing here knows about email
     * or pincode specifically.
     *
     * Runs on every form. A captcha, when present, adds a second condition through
     * `_updateSubmitState()`; without one the fields alone decide.
     */
    _initSubmitGate() {
        this.$submitBtn = this.$el.find('[type="submit"], [type="Submit"]')
        const $btn = this.$submitBtn
        if (!$btn.length) return

        // Preserve the original label: _updateSubmitState() swaps in a "waiting"
        // label while a captcha verifies and has to be able to put this back.
        $btn.data('altcha-original-html', $btn.html())

        // "Submit only after a change" (form property, data-submit-after-change): the
        // button also waits until the visitor changed something. Two signs of a change:
        // the visible fields no longer read as they did, or the visitor typed / picked
        // something (which also covers a file upload, whose value is not serialised).
        if (this.$el.is('[data-submit-after-change]')) {
            this._initialState = this._formState()
            // Widgets (phone, passport) may rewrite a value while they start up; that is
            // not the visitor's change. Read the starting point again at first contact.
            this.$el.on('focusin pointerdown', () => {
                if (this._touched) return
                this._touched = true
                this._initialState = this._formState()
                this._updateSubmitState()
            })
            this.$el.on('input change', (e) => {
                if (e.originalEvent && e.originalEvent.isTrusted) this._userChanged = true
            })
        }

        this._updateSubmitState()

        // Button state follows the value live, so a corrected field enables the
        // button immediately.
        this.$el.on('input change', () => this._updateSubmitState())

        // The red border is applied on BLUR, not on input: flagging "a@" while
        // someone is still halfway through typing their address reads as an error
        // when it is just an unfinished value.
        //
        // Email fields are included whether or not they are required, matching the
        // gate above — an optional field holding "abc" is refused on submit, so it
        // has to be able to say so.
        this.$el.on('blur', '[required], input[type="email"]', (e) => {
            // An empty optional field is not wrong; only flag one with a value.
            if (!e.target.hasAttribute('required') && String(e.target.value ?? '').trim() === '') {
                e.target.classList.remove('ff-invalid')
                return
            }
            this._markFieldValidity(e.target)
        })

        // Click while not ready → show which fields are the problem.
        $btn.on('click', (e) => {
            // In the page editor the label is an inline-editable field. Clicking
            // it is an edit, not a submit attempt — leave the caret alone.
            if (this._isEditingLabel(e.target)) return

            if (this._btnDisabled) {
                e.preventDefault()
                e.stopImmediatePropagation()
                this._highlightInvalidFields()

                // Re-evaluate before giving up on this click. A captcha may have
                // verified between the last statechange and now, in which case
                // the stale _btnDisabled flag would swallow a click the user
                // should not have to repeat. If everything is ready, submit.
                this._updateSubmitState()
                if (!this._btnDisabled) {
                    this.$el.submit()
                }
            }
        })
    }

    _initAltchaStateMachine() {
        const widget = this.$el.find('altcha-widget')[0]
        const $btn = this.$submitBtn

        this._btnDisabled = true
        $btn.addClass('ff-btn-disabled')

        // Listen to Altcha state changes
        widget.addEventListener('statechange', (e) => {
            const state = e.detail?.state
            if (state === 'verified') {
                this.altchaReady = true
                this.$el.find('.altcha-error-msg').remove()
            } else if (state === 'error') {
                // Only show error on explicit user-triggered failure, not silent background errors
                // (e.g. code challenge escalation in invisible mode is not a user-facing error)
                const isVisible = widget.style.display !== 'none'
                if (this._altchaStarted && isVisible) {
                    this.altchaReady = false
                    this._showAltchaError()
                }
            } else if (state === 'expired' || state === 'unverified') {
                this.altchaReady = false
                this.$el.find('.altcha-error-msg').remove()
            }
            this._altchaStarted = true
            this._updateSubmitState()
        })

        // Check if already verified before listener attached (fast local PoW)
        // data-state is on the inner .altcha div, not the custom element itself
        const innerState = widget.querySelector('.altcha')?.getAttribute('data-state')
            ?? widget.getAttribute('data-state')
        if (innerState === 'verified') {
            this.altchaReady = true
            this._altchaStarted = true
        }

        // The button wiring itself (initial state, input/blur listeners, the click
        // handler) lives in _initSubmitGate(), which already ran for this form.
    }

    /**
     * The required fields that belong to THIS submit.
     *
     * `:visible` matters now that the gate runs on every form: a required field that is
     * rendered but hidden (a collapsed step, a CSS-hidden field) can never be filled in
     * or focused, so counting it would leave the button dead with nothing on screen
     * explaining why. The browser takes the same view — it skips hidden fields when
     * reporting validity.
     *
     * The two-step auth form is not affected either way: `show` filters the other
     * step's fields out server-side, so they are never in the DOM at all.
     */
    _getRequiredFields() {
        const $required = this.$el.find('[required]')
            .not('altcha-widget [required]')
            .filter(':visible')

        // An e-mail field that CARRIES A VALUE is gated too, even when it is not
        // marked required. The server takes the same view — a useremail field is
        // validated as an address whether or not it is required — so a button that
        // submits "abc" only to have it refused is a round trip for nothing.
        //
        // An EMPTY optional field is left alone: the server accepts that, so blocking
        // it here would stop a submit the back end would have taken. Only a required
        // one has to be filled, and it is already in the set above.
        const $emails = this.$el.find('input[type="email"]')
            .not('altcha-widget input')
            .filter(':visible')
            .filter((_, el) => !el.hasAttribute('required'))
            .filter((_, el) => String(el.value ?? '').trim() !== '')

        return $required.add($emails)
    }

    /**
     * Is ONE field acceptable?
     *
     * Asks the element itself via checkValidity(), so the rule is whatever the field
     * declares — `type="email"` gives format checking for free (FFQT\Useremail already
     * sets it), and `pattern` / `minlength` come along too. This is FORMAT only: the
     * browser never looks up whether a mailbox exists. Whether an address belongs to an
     * account, an order, or a suspended user is decided by the workflow after submit.
     *
     * Falls back to the old emptiness test where checkValidity is unavailable, so an
     * ancient browser keeps today's behaviour rather than locking the button.
     */
    _fieldValid(el) {
        if (!el) return true
        if (typeof el.checkValidity !== 'function') return String(el.value ?? '').trim() !== ''
        if (!el.checkValidity()) return false

        // The browser accepts a bare hostname after the @ — `hello@asdasda` passes
        // its check, because an intranet address like `user@mailserver` is legal.
        // On a public sign-up form it never is: the server then refuses it for having
        // no real domain, so the visitor makes a round trip to be told what could
        // have been said here. Require a dot with something either side of it.
        if (el.type === 'email') {
            const value = String(el.value ?? '').trim()
            if (value === '') return true   // emptiness is the required attribute's business
            const domain = value.slice(value.lastIndexOf('@') + 1)
            if (!/^[^.]+(\.[^.]+)+$/.test(domain)) return false
        }

        return true
    }

    _fieldsReady() {
        if (!this._changed()) return false
        const required = this._getRequiredFields().toArray()
        if (required.length === 0) return true
        return required.every(el => this._fieldValid(el))
    }

    /** The visible fields as one string. Hidden inputs are left out: a captcha fills one by itself. */
    _formState() {
        return this.$el.find(':input[name]').not('[type=hidden]').serialize()
    }

    /** Always true unless the form waits for a change (see _initSubmitGate). */
    _changed() {
        if (this._initialState === undefined) return true
        return this._userChanged === true || this._formState() !== this._initialState
    }

    /** Add or clear the red border on one field, per its own validity. */
    _markFieldValidity(el) {
        if (!el || !el.classList) return
        // An untouched empty field is not "wrong" yet — only flag a value the user
        // has actually entered, or a field they left empty after visiting it.
        if (this._fieldValid(el)) {
            el.classList.remove('ff-invalid')
        } else {
            el.classList.add('ff-invalid')
        }
    }

    _highlightInvalidFields() {
        this._getRequiredFields().each((_, el) => this._markFieldValidity(el))

        // Clear the red border as soon as the value becomes valid, rather than making
        // the user blur the field again to see it go.
        if (!this._invalidListenerAttached) {
            this._invalidListenerAttached = true
            this.$el.on('input change', '[required]', (e) => {
                if (this._fieldValid(e.target)) {
                    e.target.classList.remove('ff-invalid')
                }
            })
        }
    }

    _updateSubmitState() {
        const $btn = this.$submitBtn
        if (!$btn || !$btn.length) return

        const fieldsOk = this._fieldsReady()
        // Only a form that actually HAS Altcha waits for it. Testing this.altchaReady
        // directly would read undefined on every other form and leave the button
        // permanently disabled — the gate now runs everywhere, so this must be an
        // explicit check for the widget, not a truthiness test on its state.
        const altchaOk = this.captchaType === 'altcha' ? this.altchaReady : true
        const originalHtml = $btn.data('altcha-original-html')

        if (fieldsOk && altchaOk) {
            // Stage 3: Ready
            this._btnDisabled = false
            $btn.removeClass('ff-btn-disabled')
            this._setBtnHtml(originalHtml)
        } else if (fieldsOk && !altchaOk) {
            // Stage 2: Security pending
            this._btnDisabled = true
            $btn.addClass('ff-btn-disabled')
            this._setBtnHtml('<span class="altcha-spinner"></span><span>Waiting for security check...</span>')
        } else {
            // Stage 1: Fields missing
            this._btnDisabled = true
            $btn.addClass('ff-btn-disabled')
            this._setBtnHtml(originalHtml)
        }
    }

    /**
     * Is this click landing inside an inline editor on the submit label?
     *
     * True only in the page editor, where the label is wrapped in a TinyMCE /
     * Tiptap editable node. On the public site nothing matches and the normal
     * submit handling runs unchanged.
     */
    _isEditingLabel(target) {
        if (!target || !target.closest) return false
        return !!target.closest('.mce-content-body, [contenteditable="true"]')
    }

    /**
     * Replace the button's contents only when they actually differ.
     *
     * This runs on every input/change and on click, and it used to rewrite the
     * innerHTML unconditionally. In the page editor the button label is an
     * inline-editable TinyMCE field, so re-writing identical markup destroyed
     * the element the editor was attached to — the caret appeared and then
     * vanished the moment you clicked the label. Skipping the no-op write keeps
     * the editable node alive.
     */
    _setBtnHtml(html) {
        const $btn = this.$submitBtn
        if ($btn.html() === html) return

        // Never tear down a label that is being edited: TinyMCE (or Tiptap)
        // owns that subtree until the user clicks away.
        if ($btn.find('.mce-content-body, [contenteditable="true"]').length) return

        $btn.html(html)
    }

    _showAltchaError() {
        const msg = 'Security check failed. Please refresh the page or try again later.'
        if (window.notyf) {
            window.notyf.error(msg)
        } else if (window.toastr) {
            window.toastr.error(msg)
        } else {
            if (!this.$el.find('.altcha-error-msg').length) {
                this.$submitBtn.before(
                    '<div class="altcha-error-msg alert alert-danger" style="margin-top:10px">' + msg + '</div>'
                )
            }
        }
    }

    async runChallenge() {
        if (this.captchaType === 'recaptcha' && this.captchaWidgetID !== undefined) {
            await grecaptcha.execute(this.captchaWidgetID, {action: 'submit'})
        }
    }

    async beforeSubmit() {
        await this.runChallenge()
    }
}
